import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarClock, Camera, Layers, Loader2, Play, Save } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { StepEditor } from "@/components/StepEditor";
import { StatusBadge } from "@/components/StatusBadge";
import { WorkPageShell } from "@/components/WorkPageShell";
import { CountdownCell } from "@/components/CountdownCell";
import { ResnapshotProgressDialog } from "@/components/ResnapshotProgressDialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefBadge } from "@/components/RefBadge";
import { StabilityChip } from "@/components/StabilityChip";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/datetime";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function describeRecurrence(s) {
  if (s.recurrence_type === "once") {
    const when = s.run_date_local ? s.run_date_local.replace("T", " at ") : "";
    return `One-time · ${when}`;
  }
  if (s.recurrence_type === "daily") {
    return `Daily at ${s.run_time_local || ""}`;
  }
  if (s.recurrence_type === "weekly") {
    const days = (s.weekdays || []).map((d) => WEEKDAY_LABELS[d]).join(", ") || "—";
    return `Weekly on ${days} at ${s.run_time_local || ""}`;
  }
  return s.recurrence_type;
}

function ScheduledRunsCard({ schedules, suiteId }) {
  return (
    <div className="mb-10" data-testid="scheduled-runs-card">
      <h2 className="text-sm font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
        <CalendarClock size={14} className="text-[#3B82F6]" />
        Scheduled runs ({schedules.length})
      </h2>
      <div className="surface-card divide-y divide-[#F1F5F9]">
        {schedules.map((s) => {
          const inherited = s.entity_type === "suite";
          return (
            <div
              key={s.id}
              className="flex items-start justify-between gap-4 px-4 py-3"
              data-testid={`scheduled-run-row-${s.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-[#0F172A]">
                    {describeRecurrence(s)}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-[#64748B] bg-[#F1F5F9] px-1.5 py-0.5 rounded">
                    {s.timezone}
                  </span>
                  {inherited && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#1D4ED8] bg-[#EFF6FF] border border-[#BFDBFE] px-1.5 py-0.5 rounded"
                      title="This test runs as part of a scheduled suite"
                      data-testid={`scheduled-run-inherited-${s.id}`}
                    >
                      <Layers size={10} />
                      via suite
                    </span>
                  )}
                </div>
                {s.owner_email && (
                  <p className="text-xs text-[#94A3B8] mt-1">Created by {s.owner_email}</p>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] uppercase tracking-wide text-[#94A3B8]">Next run</div>
                <CountdownCell
                  targetIso={s.next_run_at_utc}
                  status={s.status}
                  testId={`scheduled-run-countdown-${s.id}`}
                />
                <div className="text-[11px] text-[#94A3B8] mt-0.5">
                  {s.next_run_at_utc ? formatDateTime(s.next_run_at_utc) : ""}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {schedules.some((s) => s.entity_type === "suite") && suiteId && (
        <p className="text-[11px] text-[#94A3B8] mt-2">
          Schedules marked <span className="font-medium text-[#1D4ED8]">via suite</span> are
          inherited from the parent suite and trigger this test alongside every other test in that
          suite.
        </p>
      )}
    </div>
  );
}

export default function TestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [runs, setRuns] = useState([]);
  const [authStates, setAuthStates] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [resnapping, setResnapping] = useState(false);
  // Live progress state for the re-snapshot dialog. `null` means the
  // dialog is closed; an object means it's open and being updated
  // every poll tick by `handleResnapshot`.
  const [resnapJob, setResnapJob] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, r, a] = await Promise.all([
        api.get(`/tests/${id}`),
        api.get(`/tests/${id}/runs`),
        api.get("/auth-states"),
      ]);
      setTest(t.data);
      setRuns(r.data);
      setAuthStates(a.data);
      // Fetch schedules: any active schedule on this test, plus any active
      // schedule on its parent suite (the test runs inherited via suite).
      try {
        const { data: all } = await api.get("/schedules?status=active");
        const items = all.items || [];
        const matched = items.filter((s) => {
          if (s.entity_type === "test" && s.entity_id === t.data.id) return true;
          if (s.entity_type === "suite" && t.data.suite_id && s.entity_id === t.data.suite_id) return true;
          return false;
        });
        // Sort by soonest next_run_at_utc first
        matched.sort((a, b) => (a.next_run_at_utc || "").localeCompare(b.next_run_at_utc || ""));
        setSchedules(matched);
      } catch (_e) {
        setSchedules([]);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/tests/${id}`, {
        name: test.name,
        target_url: test.target_url,
        auth_state_id: test.auth_state_id || "",
        steps: test.steps,
      });
      setTest(data);
      toast.success("Test saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    const NEEDS_SELECTOR = ["click", "fill", "assert_visible"];
    const missing = test.steps.findIndex(
      (s) => (NEEDS_SELECTOR.includes(s.action) && !s.selector?.trim()) || (s.action === "goto" && !s.url?.trim())
    );
    if (missing !== -1) {
      toast.error(`Step ${missing + 1}: ${test.steps[missing].action === "goto" ? "a URL" : "a selector"} is required`);
      return;
    }
    setRunning(true);
    try {
      await api.put(`/tests/${id}`, {
        name: test.name,
        target_url: test.target_url,
        auth_state_id: test.auth_state_id || "",
        steps: test.steps,
      });
      const { data: run } = await api.post(`/tests/${id}/run`);
      navigate(`/runs/${run.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setRunning(false);
    }
  };

  // Backfill record-time snapshots for this test by walking through
  // its steps in a headless browser. Used for tests recorded before
  // recording-time snapshots shipped, or to refresh the baseline when
  // the target UI has changed. The backend silently executes each step
  // (clicks/fills/navs fire so the page reaches the right state) but
  // never asserts — every step's post-action snapshot is persisted
  // under `source="record"`.
  //
  // Iteration 30: kicks off a *background* job on the backend and
  // polls for progress. The synchronous version was killed by the
  // production proxy's 120-second `Proxy Read Timeout` cap on long
  // tests (e.g. 35-step Insurance Offline Approval).
  //
  // Iteration 31: drives a live progress *modal* (ResnapshotProgressDialog)
  // instead of a single toast — gives users a real progress bar,
  // captured/skipped/failed counters, and a final per-step error list
  // when the job finishes. Modal stays open until the user dismisses
  // it post-completion.
  const handleResnapshot = async () => {
    if (!test || resnapping) return;
    if (!test.steps?.length) {
      toast.error("This test has no steps to snapshot");
      return;
    }
    setResnapping(true);
    setResnapJob({
      status: "running",
      total: test.steps.length,
      captured: 0,
      skipped: 0,
      failed: 0,
      current: 0,
      errors: [],
    });
    try {
      const { data: kickoff } = await api.post(`/tests/${id}/resnapshot`);
      const jobId = kickoff.job_id;
      const totalSteps = kickoff.total_steps ?? test.steps.length;
      if (!jobId) {
        setResnapJob({
          status: "done",
          total: 0,
          captured: 0,
          skipped: 0,
          failed: 0,
          current: 0,
          errors: [],
        });
        return;
      }
      setResnapJob((prev) => ({ ...(prev || {}), total: totalSteps }));
      // Poll every 2 s. Cap the watch at ~30 minutes so a stuck job
      // doesn't keep the dialog spinning forever; the backend job
      // continues independently and the user can refresh to see its
      // outcome.
      const POLL_MS = 2000;
      const MAX_POLLS = 900; // 30 min
      for (let i = 0; i < MAX_POLLS; i += 1) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        const { data: status } = await api.get(
          `/tests/${id}/resnapshot/jobs/${jobId}`,
        );
        setResnapJob({
          status: status.status,
          total: status.total_steps ?? totalSteps,
          captured: status.captured ?? 0,
          skipped: status.skipped ?? 0,
          failed: status.failed ?? 0,
          current: status.current_index ?? 0,
          errors: status.errors ?? [],
          fatalError: status.error || undefined,
        });
        if (status.status === "done" || status.status === "failed") {
          return;
        }
      }
      // Timed out polling — leave the dialog visible with a soft
      // warning. The backend job may still finish; user can refresh.
      setResnapJob((prev) => ({
        ...(prev || {}),
        status: "failed",
        fatalError: "Still running after 30 minutes — refresh later to see results.",
      }));
    } catch (e) {
      // Network or auth failure surfaced as a fatal banner in the
      // modal rather than a separate toast — keeps the UX single-
      // surface.
      setResnapJob((prev) => ({
        ...(prev || { total: 0, captured: 0, skipped: 0, failed: 0, current: 0, errors: [] }),
        status: "failed",
        fatalError: formatApiError(e),
      }));
    } finally {
      setResnapping(false);
    }
  };

  const closeResnapshotDialog = () => setResnapJob(null);

  if (!test) {
    return (
      <WorkPageShell>
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 text-sm text-[#64748B]">Loading…</div>
      </WorkPageShell>
    );
  }

  return (
    <WorkPageShell
      backTo={test.project_id ? `/projects/${test.project_id}/tests` : "/tests"}
      backLabel="All tests"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="test-detail-page">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-8">
          <div className="flex-1 max-w-xl space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <RefBadge value={test.ref} testId="test-detail-ref" />
              <StabilityChip steps={test.steps} testId="test-detail-stability" />
            </div>
            <Input
              data-testid="test-name-input"
              value={test.name}
              onChange={(e) => setTest({ ...test, name: e.target.value })}
              className="rounded-md border-[#E5E7EB] font-['Geist'] font-semibold text-lg h-11 focus-visible:ring-[#3B82F6] focus-visible:border-[#3B82F6]"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-[#64748B]">Target URL</label>
                <Input
                  data-testid="test-url-input"
                  value={test.target_url}
                  onChange={(e) => setTest({ ...test, target_url: e.target.value })}
                  className="rounded-md border-[#E5E7EB] font-mono text-xs h-9 focus-visible:ring-[#3B82F6] focus-visible:border-[#3B82F6]"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-[#64748B]">Login state</label>
                <Select
                  value={test.auth_state_id || "none"}
                  onValueChange={(v) => setTest({ ...test, auth_state_id: v === "none" ? null : v })}
                >
                  <SelectTrigger className="rounded-md border-[#E5E7EB] h-9 text-sm focus:ring-[#3B82F6]" data-testid="test-auth-state-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-md border-[#E5E7EB]">
                    <SelectItem value="none">None — start logged out</SelectItem>
                    {authStates.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={handleResnapshot}
              disabled={resnapping || !test.steps?.length}
              data-testid="resnapshot-test-button"
              title="Walk through recorded steps in a headless browser to refresh baseline snapshots used for failure-diff diagnostics."
              className="flex items-center gap-1.5 px-3 h-10 text-sm font-medium border border-[#E5E7EB] bg-white text-[#0F172A] rounded-md hover:bg-[#F8FAFC] hover:border-[#CBD5E1] transition-colors disabled:opacity-60"
            >
              {resnapping ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
              <span className="hidden sm:inline">Re-snapshot</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-test-button"
              className="flex items-center gap-1.5 px-4 h-10 text-sm font-medium border border-[#E5E7EB] bg-white text-[#0F172A] rounded-md hover:bg-[#F8FAFC] hover:border-[#CBD5E1] transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save
            </button>
            <button
              onClick={handleRun}
              disabled={running || test.steps.length === 0}
              data-testid="run-test-button"
              className="flex items-center gap-1.5 px-4 h-10 text-sm font-semibold bg-[#3B82F6] text-white rounded-md hover:bg-[#2563EB] transition-colors disabled:opacity-60 shadow-sm"
            >
              {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
              Run test
            </button>
          </div>
        </div>

        {schedules.length > 0 && (
          <ScheduledRunsCard schedules={schedules} suiteId={test.suite_id} />
        )}

        <div className="mb-10">
          <h2 className="text-sm font-semibold text-[#0F172A] mb-3">Steps ({test.steps.length})</h2>
          <StepEditor
            steps={test.steps}
            onChange={(steps) => setTest({ ...test, steps })}
            testId={test.id}
            onMutated={load}
          />
        </div>

        <div>
          <h2 className="text-sm font-semibold text-[#0F172A] mb-3">Run history</h2>
          {runs.length === 0 ? (
            <div className="surface-card p-6 text-sm text-[#64748B]" data-testid="no-runs-message">
              This test hasn&apos;t been run yet.
            </div>
          ) : (
            <div className="surface-card overflow-x-auto" data-testid="runs-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F8FAFC] border-b border-[#E5E7EB] text-left">
                    <th className="px-4 py-3 text-xs font-medium text-[#64748B]">Status</th>
                    <th className="px-4 py-3 text-xs font-medium text-[#64748B]">Started</th>
                    <th className="px-4 py-3 text-xs font-medium text-[#64748B]">Duration</th>
                    <th className="px-4 py-3 text-xs font-medium text-[#64748B]">Failure</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id} className="border-b border-[#F1F5F9] last:border-b-0 hover:bg-[#F8FAFC] transition-colors">
                      <td className="px-4 py-3"><StatusBadge status={r.status} testId={`run-status-${r.id}`} /></td>
                      <td className="px-4 py-3 text-xs text-[#64748B]">{formatDateTime(r.started_at)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[#334155]">{r.duration_ms != null ? `${(r.duration_ms / 1000).toFixed(1)}s` : "—"}</td>
                      <td className="px-4 py-3 text-xs text-[#B91C1C] max-w-[280px] truncate">{r.error || ""}</td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/runs/${r.id}`}
                          data-testid={`view-run-link-${r.id}`}
                          className="text-xs font-medium text-[#3B82F6] hover:text-[#2563EB] hover:underline"
                        >
                          View report →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <ResnapshotProgressDialog
        open={resnapJob !== null}
        job={resnapJob}
        onClose={closeResnapshotDialog}
      />
    </WorkPageShell>
  );
}
